/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from "react";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { 
  Controller,
  UseFormClearErrors,
  Control,
} from "react-hook-form";
import { useSelector } from "react-redux";

// Define interfaces for form data and props
interface RowFormData {
  storageType: string;
  rowType: string;
  rowNumbers: string;
  bayType: string;
  bayNumbers: string;
  bayLevelType: string;
  bayLevelNumbers: string;
  gapType: string;
}

const MASTER_BAY_GAP = [
  { id: 0, text: "After 1 Bay", value: "1" },
  { id: 1, text: "After 2 Bay", value: "2" },
  { id: 2, text: "After 3 Bay", value: "3" },
  { id: 3, text: "After 4 Bay", value: "4" },
  { id: 4, text: "After 5 Bay", value: "5" },
];

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<RowFormData>;
  control: Control<RowFormData>;
  zoneItem?: any;
  modalType?: string;
  handleSubmit: React.MouseEventHandler<HTMLButtonElement>;
  isPending: boolean;
}

interface CustomSelectProps {
  namePrefix: string;
  nameNumber: string;
  control: Control<RowFormData>;
  labeltext: string;
  options: string[];
  placeholder?: string;
  prefix: string;
  setPrefix: (val: string) => void;
  error?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  namePrefix,
  nameNumber,
  control,
  labeltext,
  options,
  placeholder = "Select...",
  prefix,
  setPrefix,
  error,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1">
      <label className="text-[#1E293B] text-sm">{labeltext}</label>
      <div className="flex items-center justify-between px-4 py-1.5 text-sm rounded-full border border-gray-200 bg-white gap-2">
        {/* Prefix Input */}
        <Controller
          name={namePrefix as keyof RowFormData}
          control={control}
          rules={{
            required: `${labeltext} prefix is required`,
            minLength: {
              value: 2,
              message: `${labeltext} prefix must be at least 2 characters`,
            },
            maxLength: {
              value: 3,
              message: `${labeltext} prefix must be 3 characters or less`,
            },
          }}
          render={({ field }) => (
            <input
              type="text"
              value={prefix}
              placeholder="Enter name"
              maxLength={3}
              onChange={(e) => {
                setPrefix(e.target.value);
                field.onChange(e.target.value);
              }}
              className="focus:outline-none w-20"
            />
          )}
        />

        {/* Dropdown Number */}
        <Controller
          name={nameNumber as keyof RowFormData}
          control={control}
          rules={{ required: `${labeltext} number is required` }}
          render={({ field }) => (
            <div className="relative">
              <button
                type="button"
                className="w-24 flex justify-between items-center border border-gray-300 rounded-lg p-2 py-0.5 bg-white text-gray-700 gap-1"
                onClick={() => setOpen(!open)}
              >
                <span className="font-medium text-[#212121]">
                  {field.value || placeholder}
                </span>
                <IconChevronDown
                  className={`w-4 h-4 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>
              {open && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {options.map((opt, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        field.onChange(opt);
                        setOpen(false);
                      }}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      {opt}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        />
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
};

export default function CreateRowModal({
  open,
  onClose,
  clearErrors,
  register,
  errors,
  setValue,
  modalType,
  watch,
  control,
  isPending,
  handleSubmit,
}: AssignDriversModalProps) {
  const [rackType, setRackType] = useState("");
  const [bayType, setBayType] = useState("");
  const [levelType, setLevelType] = useState("");
  const profile = useSelector((state: any) => state.auth.profile);
  const clientType = profile?.clientType;

  const StorageType = [
    { id: 0, text: "Shelves", value: "SHELVES" },
    { id: 1, text: "Bins", value: "BINS" },
  ];

  const visibleStorageTypes = StorageType.filter((item) => {
    if (clientType === "LM") return item.value === "SHELVES";
    if (clientType === "FM") return item.value === "BINS";
    if (clientType === "BOTH") return true;
    return false;
  });

  const bayNumbers = watch("bayNumbers");
  const parsedBayNumbers = bayNumbers ? parseInt(bayNumbers) : 0;
  const numGaps = parsedBayNumbers > 0 ? parsedBayNumbers - 1 : 0;
  const BayGap = MASTER_BAY_GAP.slice(0, numGaps);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[#F7F6F2] rounded-3xl w-[650px] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-medium text-[#212121]">
            {modalType === "edit" ? "Edit Row" : "Create Row"} 
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full border-[0.5px] border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex-1 p-4 space-y-1">
          <div className="grid grid-cols-2 gap-4">
            {/* Rows */}
            <CustomSelect
              namePrefix="rackType"
              nameNumber="rackNumbers"
              control={control}
              labeltext="Rack"
              options={["1", "2", "3", "4", "5", "6", "7", "8"]}
              prefix={rackType}
              setPrefix={setRackType}
              error={ errors?.rackType?.message || errors?.rackNumbers?.message}
            />

            <CustomSelect
              namePrefix="rackLevelType"
              nameNumber="rackLevelNumbers"
              control={control}
              labeltext="Level"
              options={["1", "2", "3", "4"]}
              prefix={levelType}
              setPrefix={setLevelType}
              error={
                errors?.rackLevelType?.message ||
                errors?.rackLevelNumbers?.message                 
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 p-4 border-t">
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition flex items-center justify-center"
          >
            {isPending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>{modalType === "edit" ? "Update" : "Create"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
